<template>
    <NuxtLayout :name="layout">
        <div>
            <VJoyTemplate>
                <template v-if="!isWebView" #template-back>
                    <VJoyIconButton icon="arrow-left" variant="ghost" @click="goBack" />
                </template>

                <template #template-title>
                    <div>{{ t('freelancer.charter.screen.main.title') }}</div>
                </template>
                <template #template-subtitle>
                    <div>{{ t('freelancer.charter.screen.main.subtitle') }}</div>
                </template>
                <template #template-actions>
                    <div class="freelancer-charter-illustr">
                        <MaltImg src="/rebranding2020/illustrations/meet-and-contractualize.png?vh=1f101c" width="285" alt="hero image" />
                    </div>
                </template>

                <template #template-main>
                    <VJoyWrapper v-if="charter.blocks.length > 0">
                        <section v-for="(block, index) in charter.blocks" :key="`${block.type}-${index}`" class="freelancer-charter-commitment">
                            <VJoyWrapper wrap="nowrap" direction="row">
                                <VJoyIcon :name="block.icon as JoyIconsId" size="medium" class="freelancer-charter-commitment__icon" />
                                <div>
                                    <h3>{{ block.title }}</h3>
                                    <div>{{ block.body }}</div>
                                </div>
                            </VJoyWrapper>
                        </section>
                    </VJoyWrapper>

                    <VJoyWrapper v-if="canSignCharter" justify="center">
                        <section class="freelancer-charter-signature">
                            <VJoyWrapper direction="column">
                                <div>
                                    {{ t('freelancer.charter.screen.cta.info') }}
                                </div>
                                <VJoyButton variant="main" :loading="loading" data-testid="freelancer-charter-signature" @click="sign">
                                    {{ t('freelancer.charter.screen.cta.label') }}
                                </VJoyButton>
                            </VJoyWrapper>
                        </section>
                    </VJoyWrapper>

                    <VJoyHighlight :display-icon="true" class="footer" icon="hand-check" level="info">
                        <template #highlight-title>
                            <VJoyWrapper>
                                <div class="footer-title">{{ t('freelancer.charter.screen.footer.title') }}</div>
                            </VJoyWrapper>
                        </template>
                        <template #default>
                            <VJoyWrapper>
                                <div class="footer-subtitle">{{ t('freelancer.charter.screen.footer.body') }}</div>
                                <div v-if="!isWebView" v-html="`${t('freelancer.charter.screen.footer.link')}`"></div>
                            </VJoyWrapper>
                        </template>
                    </VJoyHighlight>
                </template>
            </VJoyTemplate>
        </div>
    </NuxtLayout>
</template>

<script setup lang="ts">
    import {computed, ref} from 'vue';
    import {definePageMeta, navigateTo, useRoute} from '@typed-router';
    import {storeToRefs} from 'pinia';
    import {MaltImg} from '#components';
    import {useAuth, useTranslation} from '#imports';
    import {useFeatureFlag} from '@malt/feature-flags';
    import {VJoyButton, VJoyIconButton, VJoyHighlight, VJoyIcon, VJoyTemplate, VJoyWrapper} from '@maltjoy/core-vue';
    import type {JoyIconsId} from '@maltjoy/icons';
    import {Configuration, FreelancerCharterApi, PublicFreelancerCharterApi} from '@api';

    type CharterBlockResource = {
        type: string;
        title: string;
        body: string;
        icon: string;
    };

    type CharterResource = {
        blocks: CharterBlockResource[];
        hasProfile: boolean;
        signed: boolean;
    };

    const {t} = useTranslation();
    const {loggedIn} = storeToRefs(useAuth());
    const charter = ref<CharterResource>({
        blocks: [],
        hasProfile: false,
        signed: false,
    });
    const route = useRoute();
    const isWebView = computed(() => route.query.displayCharterInWebView === 'true');
    const canSignCharter = computed(() => !isWebView.value && charter.value.hasProfile && !charter.value.signed);
    const loading = ref(false);

    const apiConfiguration = new Configuration({basePath: ''});
    const publicFreelancerCharterApi = new PublicFreelancerCharterApi(apiConfiguration);
    const freelancerCharterApi = new FreelancerCharterApi(apiConfiguration);

    function goBack() {
        if (!loggedIn) {
            return navigateTo('/', {external: true});
        } else {
            return navigateTo('/profile', {external: true});
        }
    }
    const profileRevamp = useFeatureFlag('profile-revamp');
    async function sign() {
        loading.value = true;
        await freelancerCharterApi.sign();
        if (profileRevamp.value) {
            sessionStorage.setItem('charterSigned', 'true');
        }
        return navigateTo('/profile', {external: true});
    }

    definePageMeta({
        layout: false,
    });
    const layout = computed(() => (isWebView.value ? 'blank' : 'default'));

    charter.value = await publicFreelancerCharterApi.getCharterWithBlocks();
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    :deep(.icon-hand-check) {
        --icon-size: 24px;
    }

    .footer {
        width: 80%;
        margin: auto;
        padding: var(--joy-core-spacing-8);

        @include mq.screen_sm {
            width: 100%;
        }

        &-title {
            font-size: var(--joy-font-size-primary-600);
        }
    }

    :deep(.joy-template__heading___headings) {
        align-self: center;
        padding-right: var(--joy-core-spacing-12);

        @include mq.screen_sm(max) {
            padding-right: unset;
        }
    }

    .freelancer-charter {
        &-illustr {
            padding-right: 50px;

            @include mq.screen_sm {
                text-align: center;
            }
        }

        &-commitment {
            @include mq.screen_sm(min) {
                flex: 1 0 calc(50% - var(--joy-core-spacing-8));
            }

            &__icon {
                align-self: flex-start;
            }
        }
    }
</style>
